/**
 * Earthworks quoted vs actual (EW-8) — React Query hooks. Every figure on the
 * screens is the API's: a write refetches the job type rather than adjusting a
 * number in the cache.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { unwrap } from '../lib/api-envelope';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import { ewAiKeys } from './useEwAiQuotes';
import type { EwQvaAccepted, EwQvaGenerated, EwQvaJob, EwQvaJobType, EwQvaSuggestion, EwQvaTypeRow } from '../types/ewQva';

export const ewQvaKeys = {
  all: ['earthworks', 'qva'] as const,
  job: (id: string) => [...ewQvaKeys.all, 'job', id] as const,
  index: () => [...ewQvaKeys.all, 'index'] as const,
  jobType: (t: string) => [...ewQvaKeys.all, 'type', t] as const,
};

export function useEwQvaJob(jobId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ewQvaKeys.job(jobId ?? ''),
    queryFn: async () => unwrap<EwQvaJob>((await api.earthworks.qva.job(jobId!)).data),
    enabled: !!jobId && enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useEwQvaIndex(enabled = true) {
  return useQuery({
    queryKey: ewQvaKeys.index(),
    queryFn: async () => {
      const d = unwrap<EwQvaTypeRow[]>((await api.earthworks.qva.index()).data);
      return Array.isArray(d) ? d : [];
    },
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useEwQvaJobType(jobType: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ewQvaKeys.jobType(jobType ?? ''),
    queryFn: async () => unwrap<EwQvaJobType>((await api.earthworks.qva.jobType(jobType!)).data),
    enabled: !!jobType && enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

function useRefreshType(jobType: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ewQvaKeys.jobType(jobType) });
    qc.invalidateQueries({ queryKey: ewQvaKeys.index() });
  };
}

export function useGenerateEwQvaSuggestions(jobType: string) {
  const refresh = useRefreshType(jobType);
  return useMutation({
    mutationFn: async () => unwrap<EwQvaGenerated>((await api.earthworks.qva.generate(jobType)).data),
    onSuccess: refresh,
  });
}

export function useAcceptEwQvaSuggestion(jobType: string) {
  const refresh = useRefreshType(jobType);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => unwrap<EwQvaAccepted>((await api.earthworks.qva.accept(id)).data),
    onSuccess: () => {
      refresh();
      // The next AI draft reads the new default; open drafts recompute on their next read.
      qc.invalidateQueries({ queryKey: ewAiKeys.all });
    },
  });
}

export function useDismissEwQvaSuggestion(jobType: string) {
  const refresh = useRefreshType(jobType);
  return useMutation({
    mutationFn: async (id: string) => unwrap<EwQvaSuggestion>((await api.earthworks.qva.dismiss(id)).data),
    onSuccess: refresh,
  });
}

/** The narrative call. A discarded or failed answer is an axios error carrying the API's reason. */
export function useEwQvaNarrative(jobType: string) {
  const refresh = useRefreshType(jobType);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.earthworks.qva.narrative(jobType)).data,
    onSuccess: () => { refresh(); qc.invalidateQueries({ queryKey: ewAiKeys.budget() }); },
    onError: () => { refresh(); qc.invalidateQueries({ queryKey: ewAiKeys.budget() }); },
  });
}
